<template>
  <div class="field-list-edit position-relative" :class="{ 'readonly-field': calculated }">
    <!-- Botón de lock/unlock para campos safe (solo si no está deshabilitado) -->
    <div v-if="field.safe && !disableLock" class="float-left">
      <button
        v-if="isLocked"
        type="button"
        class="btn btn-sm btn-outline-secondary border-0 buttonn-lock"
        @click="unlockField"
      >
        <i class="material-icons">lock</i>
      </button>
      <button
        v-else
        type="button"
        class="btn btn-outline-primary btn-sm border-0 buttonn-lock"
        @click="lockField"
      >
        <i class="material-icons">lock_open</i>
      </button>
    </div>

    <InputList
      v-if="listOptions.length > 0"
      :field="field"
      :value="normalizedValue"
      :fieldId="computedFieldId"
      :listOptions="displayListOptions"
      :disabled="field.readonly || isLocked"
      @input="onFieldChange"
    />
    <div v-else class="text-muted">
      <i class="fas fa-spinner fa-spin"></i> Cargando opciones...
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import InputList from "../../common/inputs/InputList.vue";

export default {
  name: "EditFieldList",
  components: {
    InputList,
  },
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [String, Number, Object, Array],
      default: null,
    },
    fieldId: {
      type: String,
      default: null,
    },
    editingFields: {
      type: String,
      default: "",
    },
    lists: {
      type: Array,
      default: () => [],
    },
    optionIdFilter: Array,
    disableLock: {
      type: Boolean,
      default: false,
    },
    calculated: {
      type: Boolean,
      default: false,
    },
    /** Registro en edición (p. ej. fila de grupo). Si tiene _id y el valor está vacío, no mostrar defaultValue. */
    recordItem: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      activelyEditing: false,
      listOptions: [],
      isLoading: false,
    };
  },
  computed: {
    displayValue() {
      if (this.value !== null && this.value !== undefined && this.value !== "") {
        return this.value;
      }
      if (this.shouldSkipDefaultForPersistedRecord()) {
        return null;
      }
      if (this.field.defaultValue !== undefined && this.field.defaultValue !== null) {
        return this.field.defaultValue;
      }
      return null;
    },
    computedFieldId() {
      return this.fieldId || `field-list-${this.field.key}`;
    },
    isLocked() {
      if (this.activelyEditing) {
        return false;
      }
      if (!this.value) {
        return false;
      }
      if (this.field.safe && this.editingFields !== this.field.key) {
        return true;
      }
      return false;
    },
    normalizedValue() {
      if (!this.displayValue) return this.field.isArray ? [] : null;
      if (this.field.isArray && Array.isArray(this.displayValue)) {
        return this.displayValue.map((val) => this.normalizeValue(val));
      }
      return this.normalizeValue(this.displayValue);
    },
    displayListOptions() {
      const raw = this.listOptions || [];
      if (this.optionIdFilter == null) return raw;
      if (!Array.isArray(this.optionIdFilter)) return raw;
      if (this.optionIdFilter.length === 0) return [];
      const allowedRoots = new Set(this.optionIdFilter.map(String));
      const hasRoot = raw.some((o) => o && !o.parent);
      const hasChild = raw.some((o) => o && o.parent);
      const hierarchical = hasRoot && hasChild;
      if (!hierarchical) {
        return raw.filter((opt) =>
          allowedRoots.has(String(opt._id || opt.id))
        );
      }
      const idMap = new Map(raw.map((o) => [String(o._id || o.id), o]));
      return raw.filter((opt) => {
        const rootId = this.listRootIdForOption(opt, idMap);
        return allowedRoots.has(rootId);
      });
    },
  },
  async mounted() {
    await this.loadListOptions();
  },
  watch: {
    lists: {
      handler() {
        this.loadListOptions();
      },
      deep: true,
    },
    "field.listType"() {
      this.loadListOptions();
    },
  },
  methods: {
    shouldSkipDefaultForPersistedRecord() {
      const record = this.recordItem;
      if (!record || record._id == null || record._id === "") {
        return false;
      }
      return true;
    },
    listRootIdForOption(opt, idMap) {
      let cur = opt;
      const visited = new Set();
      while (cur && cur.parent) {
        const cid = String(cur._id || cur.id);
        if (visited.has(cid)) break;
        visited.add(cid);
        cur = idMap.get(String(cur.parent));
        if (!cur) break;
      }
      return String(cur._id || cur.id);
    },
    async loadListOptions() {
      if (!this.field.listType) {
        this.listOptions = [];
        return;
      }
      const existingList = this.lists.find((l) => l.type === this.field.listType);
      if (existingList && existingList.values) {
        this.listOptions = existingList.values;
        return;
      }
      if (this.isLoading) return;
      this.isLoading = true;
      try {
        const response = await wencesApi.get(
          `/api/gotrading/lists/type/${this.field.listType}`
        );
        if (response.data && response.data.values) {
          this.listOptions = response.data.values;
        } else {
          this.listOptions = [];
        }
      } catch (error) {
        console.error(
          `Error loading list options for ${this.field.listType}:`,
          error
        );
        this.listOptions = [];
      } finally {
        this.isLoading = false;
      }
    },
    normalizeValue(value) {
      if (typeof value === "object" && value !== null && value._id) {
        return value;
      }
      const valueId = typeof value === "object" ? value._id || value.id : value;
      if (valueId && this.listOptions.length > 0) {
        const option = this.listOptions.find((opt) => {
          const optId = opt._id || opt.id;
          return optId && optId.toString() === String(valueId);
        });
        if (option) {
          return option;
        }
      }
      return {
        _id: valueId || value,
        label: valueId || value,
      };
    },
    onFieldChange(newValue) {
      this.activelyEditing = true;
      this.$emit("input", newValue);
    },
    onBlur() {
      if (this.field.safe && this.editingFields !== this.field.key) {
        this.activelyEditing = false;
      }
    },
    onFocus() {
      if (this.field.safe && this.editingFields !== this.field.key) {
        this.activelyEditing = true;
      }
    },
    lockField() {
      this.$emit("update:editingFields", "");
      this.activelyEditing = false;
    },
    unlockField() {
      this.$emit("update:editingFields", this.field.key);
      this.activelyEditing = true;
    },
  },
};
</script>

<style scoped>
.field-list-edit {
  width: 100%;
}

</style>

